// src/masterLayout/MasterLayout.jsx
import { Icon } from "@iconify/react/dist/iconify.js";
import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import ThemeToggleButton from "../helper/ThemeToggleButton";
import { usePermissions } from "../context/PermissionContext";
import { toast } from "react-toastify";
import LoadingComponent from "../components/ui/LoadingComponent";
import Notification from "../components/child/Notification";
import Profile from "../components/child/Profile";
import SubscriptionExpiredModal from "../components/SubscriptionExpiredModal";
import UsageStatisticsModal from "../components/UsageStatisticsModal";
import AttendanceToggle from "../components/AttendanceToggle";
import { LOGO_URL, PROFILE_PICTURE_URL } from "../utils/Contants";
import { routePermissions } from "../routes/ProtectedRoute";

const menuItems = [
  {
    title: "Incentive",
    icon: "lucide:chart-line",
    permission: [
      "/incentive-plan",
      "/incentive-assginment",
      "/my-incentive-wallet",
    ],
    subItems: [
      {
        title: "Incentive Plan",
        path: "/incentive-plan",
        permission: "view_incentive",
      },
      {
        title: "Incentive Assignment",
        path: "/incentive-assginment",
        permission: "view_incentive_assignment",
      },
      {
        title: "My Incentive wallet",
        path: "/my-incentive-wallet",
        permission: "my_incentive_wallet",
      },
    ],
  },
  {
    title: "Staff",
    icon: "lucide:users-round",
    permission: [
      "/staff",
      "/staff-branches",
      "/staff-roles",
      "/redeem-requestes",
    ],
    subItems: [
      {
        title: "Staff Roles",
        path: "/staff-roles",
        permission: "view_staff_roles",
      },
      {
        title: "Staff Management",
        path: "/staff",
        permission: "view_staff",
      },
      {
        title: "Staff Branches",
        path: "/staff-branches",
        permission: "view_staff_branches",
      },
      {
        title: "Redeem Requests",
        path: "/redeem-requestes",
        permission: "redeem_request",
      },
    ],
  },
  {
    title: "Reports",
    icon: "lucide:pie-chart",
    permission: ["/revenue-reports", "/staff-attendence"],
    subItems: [
      {
        title: "Revenue Reports",
        path: "/revenue-reports",
        permission: "view_revenue_report",
      },
      {
        title: "Staff Attendance",
        path: "/staff-attendence",
        permission: "view_staff_attendance",
      },
    ],
  },
  {
    title: "Location & Operations",
    icon: "mdi:map-marker-radius-outline",
    permission: ["/branch-locations", "/slots", "/branch-list"],
    subItems: [
      {
        title: "Branches",
        path: "/branch-list",
        permission: "view_client_branches",
      },
      {
        title: "Locations",
        path: "/branch-locations",
        permission: "view_branch_locations",
      },
      {
        title: "Slots",
        path: "/slots",
        permission: "view_slots",
      },
    ],
  },
  {
    title: "Customers & Billing",
    icon: "lucide:receipt-text",
    permission: [
      "/customer-profiles",
      "/subscription",
      "/vehicle-pricing",
      "/usage-statistics",
    ],
    subItems: [
      {
        title: "Customer Profiles",
        path: "/customer-profiles",
        permission: "view_customer_profile",
      },
      {
        title: "Vehicle Pricing",
        path: "/vehicle-pricing",
        permission: "view_vehicle_pricing",
      },
      {
        title: "Subscription",
        path: "/subscription",
        permission: "view_subscription",
      },
      {
        title: "Usage Statistics",
        path: "/usage-statistics",
        permission: "view_usage_history",
      },
    ],
  },
  {
    title: "Security & Access",
    icon: "lucide:key-round",
    permission: ["/assign-permission"],
    subItems: [
      // {
      //   title: "Permissions",
      //   path: "/permissions",
      //   permission: "view_client_permissions",
      // },
      {
        title: "Assign Permission",
        path: "/assign-permission",
        permission: "add_client_permission",
      },
    ],
  },
  {
    title: "Workflow",
    icon: "lucide:repeat-2",
    permission: ["/workflows", "/workflow-instances", "/workflow-assign-list"],
    subItems: [
      {
        title: "Workflows List",
        path: "/workflows",
        permission: "view_workflow",
      },
      {
        title: "Workflow Assign List",
        path: "/workflow-assign-list",
        permission: "assign_list_workflow",
      },
      {
        title: "Workflow Instance History",
        path: "/workflow-instances-history",
        permission: "view_workflow_instance_history",
      },
    ],
  },
];

const MasterLayout = ({ children }) => {
  const {
    hasPermission,
    isAuthenticated,
    isLoading,
    refreshAuthStatus,
    isAttendanceMarked,
    isSubscriptionExpired,
    usageStatistics,
  } = usePermissions();

  const navigate = useNavigate();
  const [sidebarActive, setSidebarActive] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [showUsageStatisticsModal, setShowUsageStatisticsModal] =
    useState(false);
  const location = useLocation();

  const userData = JSON.parse(localStorage.getItem("user"));
  const username = userData?.client_name || userData?.staff_name || "User";
  const role = userData?.client_role || userData?.role_name || "Role Name";
  const profile_image = userData?.profile_picture;
  const logo = userData?.logo_url;

  const attendanceCheck = !isAttendanceMarked && role !== "no_admin";
  const initials = username
    .split(" ")
    .map((name) => name[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleOverlayClick = (e) => {
    if (mobileMenu && e.target === e.currentTarget) {
      setMobileMenu(false);
    }
  };

  useEffect(() => {
    setMobileMenu(false);

    const handleDropdownClick = (event) => {
      event.preventDefault();
      const clickedDropdown = event.currentTarget.closest(".dropdown");
      if (!clickedDropdown) return;

      const isActive = clickedDropdown.classList.contains("open");

      document
        .querySelectorAll(".sidebar-menu .dropdown.open")
        .forEach((openDropdown) => {
          if (openDropdown !== clickedDropdown) {
            openDropdown.classList.remove("open");
            const submenu = openDropdown.querySelector(".sidebar-submenu");
            if (submenu) submenu.style.maxHeight = null;
          }
        });

      if (!isActive) {
        clickedDropdown.classList.add("open");
        const submenu = clickedDropdown.querySelector(".sidebar-submenu");
        if (submenu) {
          submenu.style.maxHeight = `${submenu.scrollHeight}px`;
        }
      } else {
        clickedDropdown.classList.remove("open");
        const submenu = clickedDropdown.querySelector(".sidebar-submenu");
        if (submenu) submenu.style.maxHeight = null;
      }
    };

    const dropdownTriggers = document.querySelectorAll(
      ".sidebar-menu .dropdown > a"
    );
    dropdownTriggers.forEach((trigger) => {
      trigger.addEventListener("click", handleDropdownClick);
    });

    const openActiveDropdown = () => {
      const activeLink = document.querySelector(
        ".sidebar-submenu .active-page"
      );
      if (activeLink) {
        const parentDropdown = activeLink.closest(".dropdown");
        if (parentDropdown) {
          parentDropdown.classList.add("open");
          const submenu = parentDropdown.querySelector(".sidebar-submenu");
          if (submenu) {
            submenu.style.maxHeight = `${submenu.scrollHeight}px`;
          }
        }
      }
    };

    openActiveDropdown();

    // Handle subscription expiration first
    if (
      isSubscriptionExpired &&
      location.pathname !== "/" &&
      !(
        location.pathname === "/subscription" &&
        hasPermission("view_subscription")
      )
    ) {
      setShowExpiredModal(true);
      setShowUsageStatisticsModal(false);
      navigate("/dashboard");
    } else {
      setShowExpiredModal(false);
      // Only check usage statistics if subscription is not expired
      if (
        !isSubscriptionExpired &&
        usageStatistics &&
        location.pathname !== "/" &&
        !(
          location.pathname === "/usage-statistics" &&
          hasPermission("view_usage_history")
        )
      ) {
        setShowUsageStatisticsModal(true);
        navigate("/dashboard");
      } else {
        setShowUsageStatisticsModal(false);
      }
    }

    return () => {
      dropdownTriggers.forEach((trigger) => {
        trigger.removeEventListener("click", handleDropdownClick);
      });
    };
  }, [
    location.pathname,
    isSubscriptionExpired,
    usageStatistics,
    navigate,
    hasPermission,
  ]);

  const handleLogout = async () => {
    localStorage.clear();
    await refreshAuthStatus();
    toast.success("Logout Successful");
    navigate("/sign-in", { replace: true });
  };

  const sidebarControl = () => setSidebarActive(!sidebarActive);
  const mobileMenuControl = () => setMobileMenu(!mobileMenu);
  const hasAnyPermission = (routes) =>
    routes.some((route) => hasPermission(routePermissions[route]));

  if (isLoading) return <LoadingComponent />;
  if (!isAuthenticated) return null;

  const getSidebarClass = () => {
    let classes = "sidebar";
    if (sidebarActive) classes += " active";
    if (mobileMenu) classes += " sidebar-open";
    return classes;
  };

  // Determine if access is restricted
  // const isRestricted =
  //   (isSubscriptionExpired && location.pathname !== "/dashboard" && !(location.pathname === "/subscription" && hasPermission("view_subscription"))) ||
  //   (!isSubscriptionExpired && !usageStatistics && location.pathname !== "/dashboard" && !(location.pathname === "/usage-statistics" && hasPermission("view_usage_history")));
  const isRestricted = false;
  return (
    <section
      className={mobileMenu ? "overlay active" : "overlay"}
      onClick={handleOverlayClick}
    >
      <style>
        {`
          .geolocation-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
          }

          .geolocation-overlay-content {
            background: var(--white);
            border-radius: var(--rounded-12);
            padding: var(--size-24);
            max-width: 32rem;
            width: 100%;
            text-align: center;
            box-shadow: var(--shadow-2);
          }

          .geolocation-overlay-content h3 {
            font-size: var(--font-xl);
            font-weight: 600;
            color: var(--text-primary-light);
            margin-bottom: var(--size-16);
          }

          .geolocation-overlay-content p {
            font-size: var(--font-md);
            color: var(--text-secondary-light);
          }

          .geolocation-overlay-content .retry-button,
          .geolocation-overlay-content .logout-button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: var(--size-8);
            padding: var(--size-12) var(--size-24);
            border: none;
            border-radius: var(--rounded-8);
            font-size: var(--font-md);
            font-weight: 500;
            cursor: pointer;
            transition: background 0.3s ease;
          }

          .geolocation-overlay-content .retry-button {
            background: var(--primary-600);
            color: var(--white);
          }

          .geolocation-overlay-content .retry-button:hover {
            background: var(--primary-700);
          }

          .geolocation-overlay-content .logout-button {
            background: var(--danger-600);
            color: var(--white);
          }

          .geolocation-overlay-content .logout-button:hover {
            background: var(--danger-700);
          }

          .geolocation-overlay-content .button-group {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            margin-top: 16px;
          }

          .restricted-access .sidebar,
          .restricted-access .dashboard-main {
            pointer-events: none;
            opacity: 0.5;
            transition: opacity 0.3s ease;
          }

          .subscription-expired-overlay,
          .usage-statistics-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 999;
            display: flex;
            align-items: center;
            justify-content: center;
            pointer-events: all;
          }

          .subscription-expired-overlay-content,
          .usage-statistics-overlay-content {
            background: var(--white);
            border-radius: var(--rounded-12);
            padding: var(--size-24);
            max-width: 32rem;
            width: 90%;
            text-align: center;
            box-shadow: var(--shadow-2);
            border: 1px solid var(--border-color);
          }

          .subscription-expired-overlay-content h3,
          .usage-statistics-overlay-content h3 {
            font-size: var(--font-xl);
            font-weight: 600;
            color: var(--danger-600);
            margin-bottom: var(--size-16);
          }

          .subscription-expired-overlay-content p,
          .usage-statistics-overlay-content p {
            font-size: var(--font-md);
            color: var(--text-secondary-light);
            margin-bottom: var(--size-20);
          }

          .subscription-expired-overlay-content .renew-button,
          .usage-statistics-overlay-content .view-statistics-button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: var(--size-8);
            padding: var(--size-12) var(--size-24);
            border: none;
            border-radius: var(--rounded-8);
            font-size: var(--font-md);
            font-weight: 500;
            cursor: pointer;
            transition: background 0.3s ease;
            background: var(--primary-600);
            color: var(--white);
            width: 100%;
            margin-bottom: var(--size-8);
          }

          .subscription-expired-overlay-content .renew-button:hover,
          .usage-statistics-overlay-content .view-statistics-button:hover {
            background: var(--primary-700);
          }

          .subscription-expired-overlay-content .dismiss-button,
          .usage-statistics-overlay-content .dismiss-button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: var(--size-8);
            padding: var(--size-12) var(--size-24);
            border: 1px solid var(--border-color);
            border-radius: var(--rounded-8);
            font-size: var(--font-md);
            font-weight: 500;
            cursor: pointer;
            transition: background 0.3s ease;
            background: transparent;
            color: var(--text-primary-light);
            width: 100%;
          }

          .subscription-expired-overlay-content .dismiss-button:hover,
          .usage-statistics-overlay-content .dismiss-button:hover {
            background: var(--gray-50);
          }
        `}
      </style>

      <aside
        className={`${getSidebarClass()} ${
          isRestricted ? "restricted-access" : ""
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={mobileMenuControl}
          type="button"
          className="sidebar-close-btn"
          style={isRestricted ? { pointerEvents: "none" } : {}}
        >
          <Icon icon="radix-icons:cross-2" />
        </button>
        <div className="sidebar-logo">
          <Link
            to="/dashboard"
            style={isRestricted ? { pointerEvents: "none" } : {}}
          >
            <img
              src={
                logo
                  ? logo.startsWith("http")
                    ? logo
                    : `${LOGO_URL}/${logo}`
                  : "assets/images/clbg.png"
              }
              alt="logo"
              className="light-logo"
            />
            <img
              src={
                logo
                  ? logo.startsWith("http")
                    ? logo
                    : `${LOGO_URL}/${logo}`
                  : "assets/images/clbg.png"
              }
              alt="logo"
              className="dark-logo"
            />
            <img
              src={
                logo
                  ? logo.startsWith("http")
                    ? logo
                    : `${LOGO_URL}/${logo}`
                  : "assets/images/clbg.png"
              }
              alt="logo-icon"
              className="logo-icon"
            />
          </Link>
        </div>

        <div className="sidebar-profile">
          <div className="profile-info">
            {profile_image ? (
              <div
                className="avatar-md rounded-circle text-base d-flex align-items-center justify-content-center"
                style={{ width: 40, height: 40 }}
              >
                <img
                  src={
                    profile_image.startsWith("http")
                      ? profile_image
                      : `${PROFILE_PICTURE_URL}/${profile_image}`
                  }
                  alt="Profile"
                  className="w-100 h-100 object-fit-cover rounded-circle"
                />
              </div>
            ) : (
              <div
                className="avatar-md rounded-circle bg-primary-600 text-base d-flex align-items-center justify-content-center"
                style={{ width: 40, height: 40 }}
              >
                <span className="fw-semibold fs-6">{initials}</span>
              </div>
            )}
            <div className="profile-details">
              <h6 className="text-md text-primary-light fw-semibold mb-0">
                {username}
              </h6>
              <span className="text-secondary-light fw-medium text-xs">
                {role
                  .split("_")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ")}
              </span>
            </div>
          </div>
        </div>

        <div className="sidebar-menu-area">
          <ul className="sidebar-menu">
            {hasPermission("view_dashboard") && (
              <li>
                <NavLink
                  to="/dashboard"
                  className={({ isActive }) => (isActive ? "active-page" : "")}
                  style={isRestricted ? { pointerEvents: "none" } : {}}
                >
                  <Icon icon="lucide:bar-chart-3" className="menu-icon" />
                  <span>Main Dashboard</span>
                </NavLink>
              </li>
            )}
            {hasPermission("view_home") && (
              <li>
                <NavLink
                  to="/home"
                  className={({ isActive }) => (isActive ? "active-page" : "")}
                  style={isRestricted ? { pointerEvents: "none" } : {}}
                >
                  <Icon icon="lucide:home" className="menu-icon" />
                  <span>Home Page</span>
                </NavLink>
              </li>
            )}
            {hasPermission("branches_hierarchy") && (
              <li>
                <NavLink
                  to="/branches-hierarchy"
                  className={({ isActive }) => (isActive ? "active-page" : "")}
                  style={isRestricted ? { pointerEvents: "none" } : {}}
                >
                  <Icon icon="lucide:git-branch" className="menu-icon" />
                  <span>Branch Hierarchy</span>
                </NavLink>
              </li>
            )}
            {hasPermission("receipt_ui") && ( <li>
              <NavLink
                to="/receipt"
                className={({ isActive }) => (isActive ? "active-page" : "")}
                style={isRestricted ? { pointerEvents: "none" } : {}}
              >
                <Icon icon="lucide:receipt-text" className="menu-icon" />
                <span>Receipt UI</span>
              </NavLink>
            </li>)}
           
          </ul>

          {menuItems.map(
            (menu, index) =>
              hasAnyPermission(menu.permission) && (
                <ul className="sidebar-menu" key={index}>
                  <li className="dropdown">
                    <Link
                      to="#"
                      style={isRestricted ? { pointerEvents: "none" } : {}}
                    >
                      <Icon icon={menu.icon} className="menu-icon" />
                      <span>{menu.title}</span>
                    </Link>
                    <ul className="sidebar-submenu">
                      {menu.subItems.map(
                        (subItem, subIndex) =>
                          hasPermission(subItem.permission) && (
                            <li key={subIndex}>
                              <NavLink
                                to={subItem.path}
                                className={({ isActive }) =>
                                  isActive ? "active-page" : ""
                                }
                                style={
                                  isRestricted ? { pointerEvents: "none" } : {}
                                }
                              >
                                {subItem.title}
                              </NavLink>
                            </li>
                          )
                      )}
                    </ul>
                  </li>
                </ul>
              )
          )}
        </div>
        <div className="sidebar-logout">
          <div className="d-flex align-items-center justify-content-center">
            <button
              onClick={handleLogout}
              className="logout-btn btn bg-danger-50 text-danger-600 hover-bg-danger-100 d-flex align-items-center justify-content-center gap-2 w-100"
              style={
                isRestricted ? { pointerEvents: "none", opacity: 0.6 } : {}
              }
            >
              <Icon icon="lucide:log-out" className="icon text-xl" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      <main
        className={`${
          sidebarActive ? "dashboard-main active" : "dashboard-main"
        } ${isRestricted ? "restricted-access" : ""}`}
      >
        <header className="navbar-header">
          <div className="d-flex align-items-center justify-content-between w-100">
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="sidebar-toggle"
                onClick={sidebarControl}
                style={isRestricted ? { pointerEvents: "none" } : {}}
              >
                <Icon icon="heroicons:bars-3-solid" className="icon" />
              </button>
              <button
                onClick={mobileMenuControl}
                type="button"
                className="sidebar-mobile-toggle"
                style={isRestricted ? { pointerEvents: "none" } : {}}
              >
                <Icon icon="heroicons:bars-3-solid" className="icon" />
              </button>
            </div>
            <div className="d-flex align-items-center gap-3">
              {role !== "no_admin" && !isRestricted && (
                <AttendanceToggle messageShow={{ check: true }} />
              )}
              {!isRestricted && <Notification />}
              {!isRestricted && <ThemeToggleButton />}
              {!isRestricted && <Profile />}
            </div>
          </div>
        </header>

        <div
          className="dashboard-main-body"
          style={isRestricted ? { pointerEvents: "none" } : {}}
        >
          {children}
        </div>
      </main>

      {showExpiredModal && (
        <SubscriptionExpiredModal onClose={() => setShowExpiredModal(false)} />
      )}
      {showUsageStatisticsModal && (
        <UsageStatisticsModal
          onClose={() => setShowUsageStatisticsModal(false)}
        />
      )}
    </section>
  );
};

export default MasterLayout;
